import {
  ArrowLeft,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  Circle,
} from 'lucide-react';
import { type MessageCollection } from '@sendbird/chat/groupChannel';
import { useInfiniteQuery } from '@tanstack/react-query';
import { type EmblaOptionsType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslate } from 'ra-core';
import { Spinner } from '../../../components/admin/spinner';
import { Button } from '../../../components/ui/button';
import { type Slide } from './fileMessagesToSlides';
import { getAllFileMessages } from './getAllFileMessages';
import './MediaViewer.css';
import {
  getMediaStartIndex,
  useMediaMessagesQuery,
} from './useMediaMessagesQuery';
import { usePrevNextButtons } from './usePrevNextButtons';

const carouselOptions: EmblaOptionsType = {
  dragFree: false,
  containScroll: 'keepSnaps',
};

type MediaViewerProps = {
  startSlide?: Slide;
  messageCollection: MessageCollection;
};

export const MediaViewer: React.FC<MediaViewerProps> = ({
  startSlide,
  messageCollection,
}) => {
  const translate = useTranslate();

  const startIndex = useMemo(
    () =>
      getAllFileMessages(messageCollection).findIndex(
        (msg) => msg.url === startSlide?.url
      ) + getMediaStartIndex(messageCollection),
    [startSlide, messageCollection]
  );

  const selectedIndexRef = useRef(startIndex);
  const videoRefsMap = useRef<Map<string, HTMLVideoElement>>(new Map());

  const [emblaRef, emblaApi] = useEmblaCarousel({
    ...carouselOptions,
    startIndex,
  });

  const query = useInfiniteQuery(
    useMediaMessagesQuery({
      channelUrl: messageCollection.channel.url,
      cachedMessagesLength: getAllFileMessages(messageCollection).length,
      messageCollection,
      // eslint-disable-next-line react-hooks/refs
      selectedIndex: selectedIndexRef.current,
      emblaApi,
      startSlideUrl: startSlide?.url,
    })
  );

  const files = useMemo(() => query.data.pages.flatMap((p) => p), [query.data]);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);

  const checkForLoadMore = useCallback(() => {
    if (!emblaApi) return;

    const selectedIndex = emblaApi.selectedScrollSnap();

    // Note: the 1st and last slides are loading indicators, so we need to adjust the index accordingly
    const firstMediaIndex = getMediaStartIndex(messageCollection);
    const lastMediaIndex = firstMediaIndex + files.length - 1;

    const shouldFetchMorePrevious = selectedIndex <= firstMediaIndex;
    const shouldFetchMoreNext = selectedIndex >= lastMediaIndex;

    // We start loading more messages when the user reaches the last or first media slide.
    // This means that the user may never see the loading indicator if the API responds quickly, because loading starts before the user reaches the loading indicators
    if (shouldFetchMoreNext && query.hasNextPage) {
      if (!query.isFetchNextPageError) {
        void query.fetchNextPage({ cancelRefetch: false });
      }
    }

    if (shouldFetchMorePrevious && messageCollection.hasPrevious) {
      if (!query.isFetchPreviousPageError) {
        void query.fetchPreviousPage({ cancelRefetch: false });
      }
    }
  }, [emblaApi, query, messageCollection, files.length]);

  const updateSelectedSlide = useCallback(() => {
    if (!emblaApi) return;
    const selectedIndex = emblaApi.selectedScrollSnap();
    selectedIndexRef.current = selectedIndex;

    // Pause all videos except the one in the active slide
    videoRefsMap.current.forEach((videoElement, videoUrl) => {
      const fileIndex = files.findIndex((file) => file.url === videoUrl);
      const adjustedFileIndex =
        fileIndex + getMediaStartIndex(messageCollection);

      if (adjustedFileIndex !== selectedIndex) {
        videoElement.pause();
      }
    });
  }, [emblaApi, files, messageCollection]);

  useEffect(() => {
    if (!emblaApi) return;

    emblaApi.on('select', checkForLoadMore);
    emblaApi.on('select', updateSelectedSlide);

    // Initial checks
    checkForLoadMore();
    updateSelectedSlide();

    return () => {
      emblaApi.off('select', checkForLoadMore);
      emblaApi.off('select', updateSelectedSlide);
    };
  }, [emblaApi, checkForLoadMore, updateSelectedSlide]);

  // Effect to clean up video references when component unmounts
  useEffect(() => {
    const currentVideoRefsMap = videoRefsMap.current;

    return () => {
      // Pause all videos and clear references on unmount
      currentVideoRefsMap.forEach((video) => {
        video.pause();
      });
      currentVideoRefsMap.clear();
    };
  }, []);

  return (
    <div className="mediaViewer">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={translate('chat.mediaViewer.previous')}
        onClick={onPrevButtonClick}
        disabled={prevBtnDisabled}
      >
        {query.isFetchingPreviousPage ? (
          <Spinner size="small" />
        ) : (
          <ChevronLeft className="size-8" />
        )}
      </Button>
      <div className="mediaViewer__viewport" ref={emblaRef}>
        <div className="mediaViewer__container">
          {query.hasPreviousPage && (
            <div className="mediaViewer__slide">
              <div className="mediaViewer__slide__element">
                {query.isFetchingPreviousPage ? (
                  <Spinner size="large" className="mediaViewer__loader" />
                ) : query.isFetchPreviousPageError ? (
                  <div className="mediaViewer__slide__document">
                    <p className="text-destructive">
                      {translate('chat.mediaViewer.loadMoreMediaError')}
                    </p>
                    <Button
                      type="button"
                      onClick={() => void query.fetchPreviousPage()}
                    >
                      {translate('chat.mediaViewer.loadMoreMediaRetry')}
                    </Button>
                  </div>
                ) : null}
              </div>
            </div>
          )}
          {files.map((file) => (
            <div className="mediaViewer__slide" key={file.url}>
              <div className="mediaViewer__slide__element">
                {file.type === 'video' && (
                  <>
                    <video
                      src={file.url}
                      controls
                      className="mediaViewer__video"
                      aria-label={translate('chat.mediaViewer.videoPreview', {
                        name: file.name ?? file.url,
                      })}
                      width={400}
                      ref={(element) => {
                        if (element) {
                          videoRefsMap.current.set(file.url, element);
                        } else {
                          videoRefsMap.current.delete(file.url);
                        }
                      }}
                    >
                      <track
                        kind="captions"
                        label={translate('chat.mediaViewer.noCaptions')}
                        default
                      />
                    </video>
                  </>
                )}
                {file.type === 'image' && (
                  <img
                    src={file.url}
                    alt={translate('chat.mediaViewer.imagePreview', {
                      name: file.name ?? file.url,
                    })}
                    className="mediaViewer__slide__image"
                  />
                )}
                {file.type === 'document' && (
                  <div className="mediaViewer__slide__document">
                    <p>{file.name}</p>

                    <FileText className="size-28 text-primary" />

                    <Button
                      type="button"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = file.url;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                    >
                      <Download />
                      {translate('chat.mediaViewer.download')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
          {query.hasNextPage && (
            <div className="mediaViewer__slide">
              <div className="mediaViewer__slide__element">
                {query.isFetchingNextPage ? (
                  <Spinner size="large" className="mediaViewer__loader" />
                ) : query.isFetchNextPageError ? (
                  <div className="mediaViewer__slide__document">
                    <p className="text-destructive">
                      {translate('chat.mediaViewer.loadMoreMediaError')}
                    </p>
                    <Button
                      type="button"
                      onClick={() => void query.fetchNextPage()}
                    >
                      {translate('chat.mediaViewer.loadMoreMediaRetry')}
                    </Button>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>
        <div className="mediaViewer__dots">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={translate('chat.mediaViewer.previous')}
            onClick={onPrevButtonClick}
            disabled={prevBtnDisabled}
          >
            <ArrowLeft className="size-6" />
          </Button>

          <Circle className="size-4 fill-primary text-primary" />

          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={translate('chat.mediaViewer.next')}
            onClick={onNextButtonClick}
            disabled={nextBtnDisabled}
          >
            <ArrowRight className="size-6" />
          </Button>
        </div>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={translate('chat.mediaViewer.next')}
        onClick={onNextButtonClick}
        disabled={nextBtnDisabled}
      >
        <ChevronRight className="size-8" />
      </Button>
    </div>
  );
};
